import { siteConfig } from "@/data/siteConfig";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, ArrowRight } from "lucide-react";
import SGLogo from "@/components/SGLogo";
import { Button } from "@/components/ui/button";
import { useWhatsAppLink } from "@/lib/whatsapp";

const GOLD = "#C89B3C";
const TEXT_MAIN = "rgba(255,255,255,0.85)";
const TEXT_DIM = "rgba(255,255,255,0.6)";
const DIVIDER = "rgba(255,255,255,0.08)";

const shikshaGuideLinks = [
  { to: "/about", label: "About Us" },
  { to: "/reviews-trust", label: "Reviews & Trust" },
  { to: "/contact", label: "Contact Us" },
  { to: "/blog", label: "Blogs" },
  { to: "/enquiry", label: "Apply Now" },
];

const careerClarityLinks = [
  { to: "/career-clarity/career-counseling", label: "Career Counselling & Clarity" },
  { to: "/career-clarity/dmit-based-understanding", label: "DMIT-Based Understanding" },
  { to: "/career-clarity/sg-focus-planner", label: "SG Focus Planner" },
  { to: "/career-clarity/stream-selection", label: "Course / Stream Selection" },
  { to: "/career-clarity/india-admissions-support", label: "India Admissions Support" },
  { to: "/career-clarity/study-abroad-guidance", label: "Study Abroad Guidance" },
  { to: "/study-abroad/canada", label: "Canada Study Pathway" },
  { to: "/career-clarity/visitor-dependent-visa-support", label: "Visitor Visa / Dependent Visa Support" },
  { to: "/career-clarity/sg-elevate", label: "SG Elevate" },
];

const aiTrainingLinks = [
  { to: "/services/sg-smart-switch", label: "Shiksha Guide Smart Switch" },
  { to: "/ai-trainings/ai-workshops", label: "AI Workshops" },
  { to: "/ai-trainings/ai-adoption-consulting", label: "AI Adoption Consulting" },
  { to: "/ai-trainings/digital-strategy-business-systems", label: "Digital Strategy & Business Systems Consulting" },
  { to: "/ai-trainings/workflow-design", label: "Workflow Design" },
  { to: "/ai-trainings/business-workflow-systems", label: "Business Workflow Systems" },
  { to: "/ai-trainings/digital-presence-guidance", label: "Digital Presence Guidance" },
  { to: "/ai-trainings/productivity-systems", label: "Productivity Systems" },
  { to: "/ai-trainings/productivity-execution-support", label: "Productivity & Execution Support" },
  { to: "/ai-trainings/content-presentation-training", label: "Content & Presentation Training" },
  { to: "/services/sg-sakha", label: "SG Sakha" },
];

const Footer = () => {
  const year = new Date().getFullYear();
  const waLink = useWhatsAppLink();
  return (
    <>
      {/* CTA strip above footer */}
      <section style={{ background: "linear-gradient(135deg, #0B1F33 0%, #102A43 100%)", borderTop: "1px solid rgba(200,155,60,0.18)" }}>
        <div className="container py-12 md:py-14">
          <div className="mx-auto flex max-w-6xl flex-col items-center gap-6 text-center md:flex-row md:items-center md:justify-between md:text-left">
            <div className="max-w-2xl">
              <h2 className="text-2xl font-bold tracking-tight md:text-3xl" style={{ color: "#F5F8FB", fontFamily: "'DM Serif Display', serif" }}>
                Move From Confusion to Clarity. From Clarity to Action.
              </h2>
              <p className="mt-2 text-sm leading-relaxed md:text-base" style={{ color: "rgba(245,248,251,0.78)" }}>
                Whether you need AI &amp; business enablement or career &amp; education clarity, Shiksha Guide helps you take a structured next step.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row md:shrink-0">
              <Button asChild size="lg" variant="accent">
                <Link to="/ai-trainings">Explore AI &amp; Business Enablement <ArrowRight size={16} className="ml-1" /></Link>
              </Button>
              <Button asChild size="lg" variant="hero-outline">
                <Link to="/career-clarity">Explore Career Clarity</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      <footer style={{ background: "#0B1F33" }} className="py-16 pb-28 md:pb-16">
      <div className="container">
        {/* Brand strip */}
        <div className="mx-auto mb-10 max-w-6xl">
          <Link to="/" className="mb-3 inline-flex items-center" aria-label="Shiksha Guide – Home">
            <SGLogo dark height={42} />
          </Link>
          <p className="text-xs font-semibold" style={{ color: GOLD }}>
            {siteConfig.brand.tagline}
          </p>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed" style={{ color: TEXT_MAIN }}>
            Shiksha Guide is founded by Chirag Doshi, an AI Coach for MSMEs and Career Strategist based in Surat. We deliver SG Smart Switch, career clarity, DMIT, and study abroad guidance — practical systems, founder-led delivery.
          </p>
        </div>

        {/* 4 columns */}
        <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Col 1: Shiksha Guide */}
          <div>
            <h3 className="mb-4 text-sm font-bold text-white">Shiksha Guide</h3>
            <nav className="flex flex-col gap-2.5 text-sm">
              {shikshaGuideLinks.map(({ to, label }) => (
                <Link key={to} to={to} className="transition-colors duration-200 hover:text-gold" style={{ color: TEXT_MAIN }}>
                  {label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Col 2: Career Clarity */}
          <div>
            <h3 className="mb-4 text-sm font-bold text-white">Career Clarity</h3>
            <nav className="flex flex-col gap-2.5 text-sm">
              {careerClarityLinks.map(({ to, label }) => (
                <Link key={to} to={to} className="transition-colors duration-200 hover:text-gold" style={{ color: TEXT_MAIN }}>
                  {label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Col 3: AI & Business Enablement */}
          <div>
            <h3 className="mb-4 text-sm font-bold text-white">AI &amp; Business Enablement</h3>
            <nav className="flex flex-col gap-2.5 text-sm">
              {aiTrainingLinks.map(({ to, label }) => (
                <Link key={to} to={to} className="transition-colors duration-200 hover:text-gold" style={{ color: TEXT_MAIN }}>
                  {label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Col 4: Connect */}
          <div>
            <h3 className="mb-4 text-sm font-bold text-white">Connect</h3>
            <div className="space-y-3 text-sm" style={{ color: TEXT_MAIN }}>
              <a href={`tel:${siteConfig.brand.phone}`} className="flex items-center gap-2.5 transition-colors hover:text-gold">
                <Phone size={14} style={{ color: GOLD }} /> {siteConfig.brand.phone}
              </a>
              <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 transition-colors hover:text-gold">
                <MessageCircle size={14} style={{ color: GOLD }} /> WhatsApp
              </a>
              <a href={`mailto:${siteConfig.brand.email}`} className="flex items-center gap-2.5 transition-colors hover:text-gold">
                <Mail size={14} style={{ color: GOLD }} /> {siteConfig.brand.email}
              </a>
              <div className="flex items-start gap-2.5">
                <MapPin size={14} className="mt-0.5 shrink-0" style={{ color: GOLD }} />
                <span>
                  {siteConfig.office.floor}, {siteConfig.office.building}<br />
                  {siteConfig.office.area}<br />
                  {siteConfig.office.city}
                </span>
              </div>
            </div>
            <div className="mt-5 flex gap-4">
              <a href={siteConfig.brand.instagram} target="_blank" rel="noopener noreferrer" className="text-xs font-medium transition-colors hover:text-gold" style={{ color: TEXT_MAIN }}>Instagram</a>
              <a href={siteConfig.brand.linkedIn} target="_blank" rel="noopener noreferrer" className="text-xs font-medium transition-colors hover:text-gold" style={{ color: TEXT_MAIN }}>LinkedIn</a>
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-12 border-t pt-6" style={{ borderColor: DIVIDER }}>
          <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 text-xs sm:flex-row" style={{ color: TEXT_DIM }}>
            <p>© {year} Shiksha Guide. All rights reserved.</p>
            <nav className="flex gap-5">
              <Link to="/privacy-policy" className="transition-colors hover:text-gold">Privacy Policy</Link>
              <Link to="/terms-and-conditions" className="transition-colors hover:text-gold">Terms of Use</Link>
            </nav>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
